/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  getTooltipDisplayValue,
  getTooltipDisplayDeltaValue,
  notNullorUndefined,
  parseFieldValue,
  getFormatter
} from '@carto/kepler.gl/utils'
import {
  KeplerTableField,
  LayerPopupSettings,
  PopupEvent,
  PopupField,
  PopupRow,
  PopupStyle,
  PopupValue
} from 'features/builder/types'
import { formatAggregatedField, getColumnAliasForAggregationExp } from 'features/builder/state/spatialIndexUtils'
import { ProviderType } from 'features/connections'
import { evaluateVecExpr } from 'features/builder/ui/layers/vecExprEvaluator'
import { ArtificialSchemaField } from '../builderConstants'
import TileLayer from '../KeplerGl/layers/TileLayer'
import { Dataset } from 'features/builder/state/builderSliceUtils'
import { Geometry } from 'geojson'
import { SpatialFilter, WidgetSource } from '@carto/api-client'
import { getFieldsFromTemplate } from './popupLayout'

function getTooltipDisplayValueWrapper({
  item,
  field,
  data,
  fieldIdx,
  isAggregated,
  provider
}: {
  item: PopupField
  field: KeplerTableField
  data: any
  fieldIdx: number
  isAggregated: boolean
  provider: ProviderType
}): string | null {
  if (isAggregated) {
    // TODO: supporting aggregated cells
    return null
  }

  if (data.valueAt) {
    return getTooltipDisplayValue({ item: item as any, field, data, fieldIdx })
  }

  const name = item.spatialIndexAggregation
    ? getColumnAliasForAggregationExp(item.name, item.spatialIndexAggregation, provider)
    : item.name
  const value = Array.isArray(data) ? data[fieldIdx] : data[name]

  if (!notNullorUndefined(value)) {
    return ''
  }

  return item.format ? getFormatter(item.format, field)(value) : parseFieldValue(value, field.type)
}

function getValue({
  item,
  fields,
  data,
  primaryData,
  compareType,
  isAggregated,
  provider
}: {
  item: PopupField
  fields: KeplerTableField[]
  isAggregated: boolean
  provider: ProviderType
  data: any
  primaryData?: any
  compareType: any
}): PopupValue {
  const name = item.name
  let displayValue: string | null
  let displayDeltaValue: string | null = null
  if (item.isExpression) {
    const evaluated = evaluateVecExpr(item.name, data)
    displayValue =
      evaluated !== null && evaluated !== undefined && typeof evaluated === 'number' && !isNaN(evaluated)
        ? String(evaluated)
        : null
  } else {
    const fieldIdx = fields.findIndex(({ name }: { name: string }) => name === item.name)

    if (fieldIdx < 0) {
      // Special case for static spatial index tileset. In this case the spatial index ID
      // is not a property of the datasource
      displayValue = data[name]?.toString() || null
    } else {
      const field = fields[fieldIdx]
      displayValue = getTooltipDisplayValueWrapper({ item, field, data, fieldIdx, isAggregated, provider })
      displayDeltaValue = isAggregated
        ? null
        : getTooltipDisplayDeltaValue({
            item: item as any,
            field,
            data,
            fieldIdx,
            primaryData,
            compareType
          })
    }
  }

  let url: string | undefined
  if (displayValue?.match(/^http/)) {
    url = displayValue
  }
  const asImg = /<img>/.test(name)

  return { url, asImg, displayValue, displayDeltaValue }
}

type PopupRowsParams = {
  fieldsToShow: PopupField[]
  fields: KeplerTableField[]
  data: Record<string, unknown>
  primaryData?: unknown
  compareType?: unknown
  isAggregated: boolean
  provider: ProviderType
}

export function getPopupRows({
  fieldsToShow,
  fields,
  data,
  primaryData,
  compareType,
  isAggregated,
  provider
}: PopupRowsParams): PopupRow[] {
  return fieldsToShow.map((field) => {
    const formatedName = field.spatialIndexAggregation
      ? formatAggregatedField(field.name, field.spatialIndexAggregation)
      : field.name
    const value = getValue({
      item: field,
      fields,
      data,
      primaryData,
      compareType,
      isAggregated,
      provider
    })
    return { field: { ...field, name: formatedName }, value }
  })
}

type PopupRowsAsyncParams = PopupRowsParams & {
  layer: TileLayer
  dataset: Dataset
  widgetSource: WidgetSource
  zoom: number
  spatialFilter?: SpatialFilter
}

type PopupRowsAsyncResponse = {
  geometry: Geometry
  rows: PopupRow[]
}

/**
 * Returns complete popup rows (both local and remote) after calling the
 * Picking Model API unconditionally. Inputs should be validated beforehand
 * with {@link isAsyncPopupSupported}.
 */
export async function getPopupRowsAsync({
  layer,
  dataset,
  widgetSource,
  zoom,
  spatialFilter,
  ...params
}: PopupRowsAsyncParams): Promise<PopupRowsAsyncResponse> {
  const { localFields, remoteFields } = getPopupFieldsToShow(params.fieldsToShow, params.fields, params.data)
  const remoteColumns = remoteFields.map(({ name }) => name)

  remoteColumns.push(dataset.metadata.geoColumn) // required for highlighting

  const featureIds = [params.data[ArtificialSchemaField.FEATURE_ID] as string]
  const [data] = await layer.getPickedFeatures({
    widgetSource,
    featureIds,
    columns: remoteColumns,
    zoom,
    spatialFilter
  })

  const localRows = getPopupRows({ ...params, fieldsToShow: localFields })
  const remoteRows = getPopupRows({ ...params, fieldsToShow: remoteFields, data })

  const mergedData: Record<string, PopupRow> = Object.fromEntries([
    ...localRows.map((row) => [row.field.name, row]),
    ...remoteRows.map((row) => [row.field.name, row])
  ])

  // Return rows in order of 'fieldsToShow' for UI display.
  return {
    geometry: data[dataset.metadata.geoColumn] as Geometry,
    rows: params.fieldsToShow.map((field) => mergedData[field.name])
  }
}

function getPopupFieldsToShow(
  fieldsToShow: PopupField[],
  fields: KeplerTableField[],
  data: Record<string, unknown>
): { localFields: PopupField[]; remoteFields: PopupField[] } {
  // Fields of type='string' are fetched unconditionally, we don't
  // know whether their values have been truncated.
  const stringFields = new Set<string>()
  for (const field of fields) {
    if (field.type === 'string') {
      stringFields.add(field.name)
    }
  }

  const localFields: PopupField[] = []
  for (const field of fieldsToShow) {
    if (field.name in data && !stringFields.has(field.name)) {
      localFields.push(field)
    }
  }

  const remoteFields: PopupField[] = []
  for (const field of fieldsToShow) {
    if (!(field.name in data) || stringFields.has(field.name)) {
      remoteFields.push(field)
    }
  }

  return { localFields, remoteFields }
}

/**
 * Popups for click or hover events are enabled only if preconditions are met:
 * interactions are enabled, style is not 'None', and at least one field
 * is populated in the field list or template.
 */
export function isLayerPopupEnabled(
  event: PopupEvent,
  settings?: LayerPopupSettings
): settings is LayerPopupSettings & { enabled: true } {
  if (!settings || !settings.enabled || !settings[event]) {
    return false
  }

  if (settings[event].templateMode) {
    return getFieldsFromTemplate(settings[event].template).length > 0
  }

  if (settings[event].style === PopupStyle.None) {
    return false
  }

  const fields = settings[event].fields
  return fields ? fields.length > 0 : false
}
